import React, { useEffect, useState } from 'react';
import { Breadcrumb, Button, Form, Input, Modal, Pagination, Row, Switch, Tag, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import Swal from 'sweetalert2';
import { adminService } from '../../services/admin.service';
import { PagingStyled, TableContainer, TableContentStyled } from '../../components/CustomAntStyled';
import { showMailActionResult } from '../../helpers/mailFallback';
import { useDispatch } from 'react-redux';
import { setLoading as setPageLoading } from '../../store/loading/actions';

const PAGE_SIZE = 10;
export const UserManagementPage = () => {
    const dispatch = useDispatch();
    const [users, setUsers] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [form] = Form.useForm();

    const loadUsers = async (targetPage = page) => {
        setLoading(true);
        try {
            const response = await adminService.getUsers(targetPage - 1, PAGE_SIZE);
            setUsers(response.data || []);
            setTotal(Number(response.headers['x-total-count'] || 0));
            setPage(targetPage);
        } catch (error: any) {
            message.error(error?.response?.data?.detail || 'Không thể tải danh sách user');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadUsers(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const createUser = async (values: any) => {
        dispatch(setPageLoading(true));
        try {
            const response = await adminService.createUser({
                nameLogin: values.login,
                fullName: values.fullName,
                email: values.email,
                phone: values.phone,
                password: values.password,
            });
            if (response.data?.code !== 0) {
                message.error(response.data?.message || 'Tạo user thất bại');
                return;
            }
            setShowCreate(false);
            form.resetFields();
            await loadUsers(1);
            dispatch(setPageLoading(false));
            message.success('Tạo user hệ thống thành công');
        } catch (error: any) {
            message.error(error?.response?.data?.detail || 'Tạo user thất bại');
        } finally {
            dispatch(setPageLoading(false));
        }
    };

    const changeStatus = async (user: any, activated: boolean) => {
        try {
            await adminService.updateUser({ ...user, activated });
            message.success(activated ? 'Đã kích hoạt user' : 'Đã khóa user');
            await loadUsers(page);
        } catch (error: any) {
            message.error(error?.response?.data?.detail || 'Cập nhật trạng thái thất bại');
        }
    };

    const resetPassword = async (user: any) => {
        const result = await Swal.fire({ title: `Reset mật khẩu cho ${user.login}?`, text: 'Email hướng dẫn sẽ được gửi tới user.', icon: 'question', showCancelButton: true, confirmButtonText: 'Gửi email', cancelButtonText: 'Hủy' });
        if (!result.isConfirmed) return;
        dispatch(setPageLoading(true));
        try {
            const response = await adminService.resetUserPassword(user.login);
            dispatch(setPageLoading(false));
            await showMailActionResult(response.data, 'Đã gửi email reset mật khẩu');
        } catch (error: any) {
            message.error(error?.response?.data?.detail || 'Reset mật khẩu thất bại');
        } finally {
            dispatch(setPageLoading(false));
        }
    };

    const columns: ColumnsType<any> = [
        { title: 'Tên đăng nhập', dataIndex: 'login' },
        { title: 'Họ tên', render: (_, user) => `${user.firstName || ''} ${user.lastName || ''}`.trim() || '--' },
        { title: 'Email', dataIndex: 'email' },
        { title: 'Quyền', dataIndex: 'authorities', render: (roles: string[]) => (roles || []).filter(item => item === 'ROLE_ADMIN' || item === 'ROLE_PARTNER').map(item => <Tag key={item}>{item === 'ROLE_PARTNER' ? 'Partner' : 'admin hệ thống'}</Tag>) },
        { title: 'Trạng thái', dataIndex: 'activated', render: (active: boolean, user) => <Switch checked={active} checkedChildren="Active" unCheckedChildren="Inactive" onChange={value => changeStatus(user, value)} /> },
        { title: 'Thao tác', render: (_, user) => <Button disabled={!user.activated} onClick={() => resetPassword(user)}>Reset pass</Button> },
    ];

    return <>
        <Breadcrumb items={[{ title: 'Hệ thống' }, { title: 'Quản lý user' }]} />
        <Row justify="space-between" align="middle" style={{ margin: '16px 0' }}><h2>Quản lý user</h2><Button type="primary" icon={<PlusOutlined />} onClick={() => setShowCreate(true)}>Tạo user</Button></Row>
        <TableContainer><TableContentStyled rowKey="id" loading={loading} columns={columns} dataSource={users} pagination={false} /></TableContainer>
        <PagingStyled><Pagination current={page} pageSize={PAGE_SIZE} total={total} showSizeChanger={false} onChange={targetPage => loadUsers(targetPage)} /></PagingStyled>
        <Modal title="Tạo user" open={showCreate} onCancel={() => setShowCreate(false)} onOk={() => form.submit()} okText="Tạo" cancelText="Hủy">
            <Form form={form} layout="vertical" onFinish={createUser}>
                <Form.Item name="login" label="Tên đăng nhập" rules={[{ required: true }]}><Input autoComplete="off" /></Form.Item>
                <Form.Item name="fullName" label="Họ tên" rules={[{ required: true, message: 'Vui lòng nhập họ tên' }]}><Input /></Form.Item>
                <Form.Item name="email" label="Email" rules={[{ required: true }, { type: 'email', message: 'Email không hợp lệ' }]}><Input autoComplete="off" /></Form.Item>
                <Form.Item name="phone" label="Số điện thoại" rules={[{ required: true, message: 'Vui lòng nhập số điện thoại' }]}><Input autoComplete="off" /></Form.Item>
                <Form.Item name="password" label="Mật khẩu" rules={[{ required: true, message: 'Vui lòng nhập mật khẩu' }, { min: 4, message: 'Mật khẩu phải có ít nhất 4 ký tự' }]}><Input.Password autoComplete="new-password" /></Form.Item>
            </Form>
        </Modal>
    </>;
};
